import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('parteezy_cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem('parteezy_cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('parteezy_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Available packages
  const packages = [
    {
      id: 'premium-monthly',
      name: 'Premium Monthly',
      description: 'All premium features for one month',
      price: 4.99,
      priceId: 'price_premium_monthly', // Stripe price ID
      features: [
        'Unlimited wheel segments (vs 6 free)',
        'Remove annoying ads',
        'Save your custom wheels',
        'Private rooms with passwords',
        'More than 4 players per game',
        'Premium wheel themes',
        'Faster loading & priority servers'
      ],
      badge: 'Most Popular',
      type: 'subscription',
      interval: 'month'
    },
    {
      id: 'premium-yearly',
      name: 'Premium Yearly',
      description: 'All premium features for one year (2 months free!)',
      price: 49.99,
      originalPrice: 59.88,
      priceId: 'price_premium_yearly', // Stripe price ID
      features: [
        'Unlimited wheel segments (vs 6 free)',
        'Remove annoying ads',
        'Save your custom wheels',
        'Private rooms with passwords',
        'More than 4 players per game',
        'Premium wheel themes',
        'Faster loading & priority servers',
        'Save 2 months - Best Value!'
      ],
      badge: 'Best Value',
      type: 'subscription',
      interval: 'year'
    },
    {
      id: 'premium-lifetime',
      name: 'Premium Lifetime',
      description: 'One-time payment for lifetime access',
      price: 99.99,
      priceId: 'price_premium_lifetime', // Stripe price ID
      features: [
        'Unlimited wheel segments (vs 6 free)',
        'Remove annoying ads FOREVER',
        'Save unlimited custom wheels',
        'Private rooms with passwords',
        'Unlimited players per game',
        'All premium wheel themes',
        'Priority servers & faster loading',
        'Lifetime access - Pay once, own forever',
        'No monthly fees ever again'
      ],
      badge: 'Lifetime Deal',
      type: 'one_time',
      interval: null
    }
  ];

  // Add item to cart
  const addToCart = (packageItem) => {
    setCartItems(prevItems => {
      // For subscriptions, only allow one subscription at a time
      if (packageItem.type === 'subscription') {
        // Remove any existing subscriptions and add the new one
        const nonSubscriptionItems = prevItems.filter(item => item.type !== 'subscription');
        return [...nonSubscriptionItems, { ...packageItem, quantity: 1 }];
      }
      
      // Check if item already exists
      const existingItem = prevItems.find(item => item.id === packageItem.id);
      
      if (existingItem) {
        // For one-time purchases, increase quantity
        return prevItems.map(item =>
          item.id === packageItem.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item
        return [...prevItems, { ...packageItem, quantity: 1 }];
      }
    });
    
    // Auto-open cart when item is added
    setIsCartOpen(true);
  };

  // Remove item from cart
  const removeFromCart = (packageId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== packageId));
  };

  // Update item quantity
  const updateQuantity = (packageId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(packageId);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === packageId
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
    setIsCartOpen(false);
  };

  // Calculate totals
  const cartTotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  // Get cart summary
  const getCartSummary = () => {
    return {
      items: cartItems,
      total: cartTotal,
      itemCount: cartItemCount,
      isEmpty: cartItems.length === 0
    };
  };

  // Create Stripe checkout session
  const createCheckoutSession = async (successUrl, cancelUrl) => {
    setLoading(true);
    
    try {
      // In a real app, this would call your backend API
      // For now, we'll use Stripe's client-side checkout
      const stripe = await stripePromise;
      
      const lineItems = cartItems.map(item => ({
        price: item.priceId,
        quantity: item.quantity,
      }));

      const { error } = await stripe.redirectToCheckout({
        lineItems,
        mode: cartItems.some(item => item.type === 'subscription') ? 'subscription' : 'payment',
        successUrl: successUrl || `${window.location.origin}/payment/success`,
        cancelUrl: cancelUrl || `${window.location.origin}/payment/cancel`,
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // State
    cartItems,
    isCartOpen,
    loading,
    packages,
    
    // Computed values
    cartTotal,
    cartItemCount,
    
    // Actions
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    setIsCartOpen,
    getCartSummary,
    createCheckoutSession,
    
    // Stripe
    stripePromise,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 